import { useCallback, useState } from "react";

const storagePrefix = "regulator:presentation:";

export function presentationStateKey({ lifetime, name, page, profileId, scope }) {
  if (scope === "profile" && !profileId) return null;
  const owner = scope === "profile" ? `profile:${encodeURIComponent(profileId)}` : "application";
  return `${storagePrefix}${lifetime}:${owner}:${page}:${name}`;
}

export function readPresentationState(options, fallback, validate = (value) => value) {
  const key = presentationStateKey(options);
  if (!key) return fallback;
  try {
    const value = JSON.parse(storageFor(options.lifetime)?.getItem(key));
    return validate(value) ?? fallback;
  } catch {
    return fallback;
  }
}

export function writePresentationState(options, value) {
  const key = presentationStateKey(options);
  if (!key) return;
  try {
    storageFor(options.lifetime)?.setItem(key, JSON.stringify(value));
  } catch {
    // Presentation state remains usable in memory when web storage is unavailable.
  }
}

export function clearProfilePresentationState(profileIds) {
  for (const lifetime of ["session", "persistent"]) {
    const storage = storageFor(lifetime);
    if (!storage) continue;
    try {
      const prefixes = profileIds.map((profileId) => `${storagePrefix}${lifetime}:profile:${encodeURIComponent(profileId)}:`);
      const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index));
      for (const key of keys) {
        if (key && prefixes.some((prefix) => key.startsWith(prefix))) storage.removeItem(key);
      }
    } catch {
      // Removing profile data must still succeed when web storage is unavailable.
    }
  }
  try {
    for (const profileId of profileIds) globalThis.localStorage?.removeItem(`regulator:compliance-expansion:${profileId}`);
  } catch {
    // Legacy presentation state cleanup is best effort.
  }
}

export function usePresentationState({ defaultValue, validate, ...options }) {
  const key = presentationStateKey(options);
  const { lifetime, name, page, profileId, scope } = options;
  const [state, setState] = useState(() => ({ key, value: readPresentationState(options, defaultValue, validate) }));
  const value = state.key === key ? state.value : readPresentationState(options, defaultValue, validate);

  const update = useCallback((nextValue) => {
    setState((current) => {
      const currentValue = current.key === key ? current.value : readPresentationState({ lifetime, name, page, profileId, scope }, defaultValue, validate);
      const resolved = typeof nextValue === "function" ? nextValue(currentValue) : nextValue;
      writePresentationState({ lifetime, name, page, profileId, scope }, resolved);
      return { key, value: resolved };
    });
  }, [defaultValue, key, lifetime, name, page, profileId, scope, validate]);

  return [value, update];
}

function storageFor(lifetime) {
  return lifetime === "persistent" ? globalThis.localStorage : globalThis.sessionStorage;
}
